import { groupBy, upperFirst } from "lodash-es";
import { ReactNode, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ActiveFilterBar } from "~/components/Filter/ActiveFilterBar/ActiveFilterBar";
import {
  ActiveFilterButton,
  ActiveFilterButtonProps,
} from "~/components/Filter/ActiveFilterButton/ActiveFilterButton";
import {
  FilterBar,
  FilterBarProps,
} from "~/components/Filter/FilterBar/FilterBar";
import {
  FilterForm,
  FilterFormProps,
} from "~/components/Filter/FilterForm/FilterForm";
import { QuickFilterBar } from "~/components/Filter/QuickFilterBar/QuickFilterBar";
import {
  QuickFilterButton,
  QuickFilterButtonProps,
} from "~/components/Filter/QuickFilterButton/QuickFilterButton";
import { ChevronRight as ChevronRightIcon } from "@mui/icons-material";
import {
  FindingFilterType,
  GetFindingsQueryVariables,
  ScoreRating,
  ScoreStateFilter,
  FindingType,
  useGetFindingsFilterSuggestionsLazyQuery,
} from "~/operations";
import { FilterMenuOption } from "~/components/Filter/FilterMenu";
import { excludeFilterByKey } from "~/lib/filterUtils";
import {
  emptyMenuOption,
  loadingMenuOption,
} from "~/components/Filter/QuickFilterMenu/QuickFilterMenu";
import { ALLOWED_FINDING_TYPES } from "./constants";

export type AssetAllFindingsFilterVariables = Pick<
  NonNullable<GetFindingsQueryVariables["filter"]>,
  "rating" | "policyMrn" | "riskFactors" | "queryTerms" | "state" | "types"
>;

export type UseAssetAllFindingsFilterContextParams = {
  assetMrn: string;
  resetCursorUrl: (href: string | URL) => URL;
  baseQueryFilters: GetFindingsQueryVariables["filter"];
};

export type UseAssetAllFindingsFilterContextOutput = {
  filterVariables: AssetAllFindingsFilterVariables;
  filterForm: ReactNode;
  filterBar: ReactNode;
  quickFilterBar: ReactNode;
  activeFilterBar: ReactNode;
  activeFilterCount: number;
  resetFilters: () => void;
};

export function useAssetAllFindingsFilterContext({
  assetMrn,
  resetCursorUrl,
  baseQueryFilters,
}: UseAssetAllFindingsFilterContextParams): UseAssetAllFindingsFilterContextOutput {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [searchInput, setSearchInput] = useState("");

  const typeFilterParams = searchParams.getAll("type");
  const riskRatingFilterParams = searchParams.getAll("riskRating");
  const riskFactorFilterParams = searchParams.getAll("riskFactor");
  const policyFilterParam = searchParams.get("policyMrn");
  const stateFilterParam = searchParams.get("state");
  const searchFilterParams = searchParams.getAll("queryTerm");

  const isAllowedFindingType = (value: string): value is FindingType => {
    return (ALLOWED_FINDING_TYPES as string[]).includes(value);
  };
  const typeFilters: FindingType[] =
    typeFilterParams.filter(isAllowedFindingType);
  const riskRatingFilters = riskRatingFilterParams.every((riskFactorParam) =>
    isScoreRating(riskFactorParam),
  )
    ? riskRatingFilterParams
    : undefined;
  const riskFactorFilters = riskFactorFilterParams.length
    ? riskFactorFilterParams
    : undefined;
  const policyFilter = policyFilterParam ? policyFilterParam : undefined;
  const stateFilter = isScoreStateFilter(stateFilterParam)
    ? stateFilterParam
    : undefined;
  const searchFilters = searchFilterParams.length
    ? searchFilterParams
    : undefined;

  // Build merged filters for suggestions from URL params + base
  const filtersForSuggestions: GetFindingsQueryVariables["filter"] =
    useMemo(() => {
      // Parse current URL filters first (variables declared later are referenced in deps)
      return {
        ...baseQueryFilters,
        ...(typeFilters.length ? { types: typeFilters } : {}),
        ...(riskRatingFilters?.length ? { rating: riskRatingFilters } : {}),
        ...(riskFactorFilters?.length
          ? { riskFactors: { mrns: { or: riskFactorFilters } } }
          : {}),
        ...(policyFilter ? { policyMrn: policyFilter } : {}),
        ...(stateFilter ? { state: stateFilter } : {}),
        ...(searchFilters?.length ? { queryTerms: searchFilters } : {}),
      };
    }, [
      baseQueryFilters,
      // deps correspond to URL param-derived values defined below
      // eslint-disable-next-line react-hooks/exhaustive-deps
      searchParams,
    ]);

  const filtersExcludingTypes = useMemo(
    () => excludeFilterByKey(filtersForSuggestions, ["types"]),
    [filtersForSuggestions],
  );
  const typeSuggestions = useAllFindingsTypeSuggestions(
    assetMrn,
    filtersExcludingTypes,
  );
  const filtersExcludingRating = useMemo(
    () => excludeFilterByKey(filtersForSuggestions, ["rating"]),
    [filtersForSuggestions],
  );
  const riskRatingSuggestions = useAllFindingsRiskRatingSuggestions(
    assetMrn,
    filtersExcludingRating,
  );
  const filtersExcludingRiskFactors = useMemo(
    () => excludeFilterByKey(filtersForSuggestions, ["riskFactors"]),
    [filtersForSuggestions],
  );
  const riskFactorSuggestions = useAllFindingsRiskFactorSuggestions(
    assetMrn,
    filtersExcludingRiskFactors,
  );
  const filtersExcludingPolicy = useMemo(
    () => excludeFilterByKey(filtersForSuggestions, ["policyMrn"]),
    [filtersForSuggestions],
  );
  const policySuggestions = useAllFindingsPolicySuggestions(
    assetMrn,
    filtersExcludingPolicy,
  );
  const filtersExcludingState = useMemo(
    () => excludeFilterByKey(filtersForSuggestions, ["state"]),
    [filtersForSuggestions],
  );
  const stateSuggestions = useAllFindingsStateSuggestions(
    assetMrn,
    filtersExcludingState,
  );

  const addFilter = (param: string, value: string, multiple = false) => {
    const url = resetCursorUrl(location.href);
    if (multiple) {
      if (!url.searchParams.has(param, value)) {
        url.searchParams.append(param, value);
      }
    } else {
      url.searchParams.set(param, value);
    }
    navigate(url.href.replace(url.origin, ""));
  };

  const removeFilter = (param: string, value?: string) => {
    const url = resetCursorUrl(location.href);
    url.searchParams.delete(param, value);
    navigate(url.href.replace(url.origin, ""));
  };

  const resetFilters = () => {
    const url = resetCursorUrl(location.href);
    url.searchParams.delete("type");
    url.searchParams.delete("riskRating");
    url.searchParams.delete("riskFactor");
    url.searchParams.delete("policyMrn");
    url.searchParams.delete("state");
    url.searchParams.delete("queryTerm");
    navigate(url.href.replace(url.origin, ""));
  };

  const activeFilters = [
    typeFilters.length
      ? typeFilters.map((type) => {
          return {
            label: "Type",
            value: formatTypeLabel(type),
            onClick: () => removeFilter("type", type),
          };
        })
      : [],
    riskRatingFilters
      ? riskRatingFilters.map((riskRatingFilter) => {
          return {
            label: "Risk Rating",
            value: formatRiskRatingLabel(riskRatingFilter),
            onClick: () => removeFilter("riskRating", riskRatingFilter),
          };
        })
      : [],
    riskFactorFilters
      ? riskFactorFilters.map((riskFactorFilter) => {
          return {
            label: "Risk Factor",
            value: formatRiskFactorLabel(riskFactorFilter),
            onClick: () => removeFilter("riskFactor", riskFactorFilter),
          };
        })
      : [],
    policyFilter
      ? {
          label: "Policy",
          value: formatPolicyLabel(policyFilter),
          onClick: () => removeFilter("policyMrn"),
        }
      : [],
    stateFilter
      ? {
          label: "State",
          value: formatStateLabel(stateFilter),
          onClick: () => removeFilter("state"),
        }
      : [],
    searchFilters
      ? searchFilters.map((searchFilter) => {
          return {
            label: "Search",
            value: searchFilter,
            onClick: () => removeFilter("queryTerm", searchFilter),
          };
        })
      : [],
  ].flatMap((f) => f);

  const [focusedField, setFocusedField] = useState<FilterMenuOption>();

  const fieldOptions = useAllFindingsSuggestions(
    focusedField?.field || "",
    assetMrn,
    filtersForSuggestions,
  );

  useEffect(() => {
    if ("fetchSuggestions" in fieldOptions) {
      fieldOptions.fetchSuggestions();
    }
  }, [focusedField?.field]);

  const filterBar = renderFilterBar({
    fieldOptions: fieldOptions.suggestions,
    focusedField,
    inputValue: searchInput,
    onInputValueChange: (value: string) => {
      setSearchInput(value);
      if ("fetchSuggestions" in fieldOptions) {
        fieldOptions.fetchSuggestions(value);
      }
    },
    onFocusedRemove: () => {
      setFocusedField(undefined);
    },
    onFilterSelect: (filter) => {
      setSearchInput("");
      if (!focusedField) {
        setFocusedField(filter);
      } else {
        setFocusedField(undefined);
        if (filter.param) {
          addFilter(filter.param, filter.value, filter.isMultiple);
        }
      }
    },
    onFilterCreate: (filter) => {
      setFocusedField(undefined);
      setSearchInput("");
      addFilter("queryTerm", filter, true);
    },
  });

  const quickMenuOptions = (param: string, options: FilterMenuOption[]) => {
    return options.map((o) => ({
      ...o,
      onSelect: () => addFilter(param, o.value, o.isMultiple),
    }));
  };

  const quickFilterBar = renderQuickFilterBar([
    {
      label: "Type",
      activeCount: typeFilters.length,
      menuOptions: quickMenuOptions("type", typeSuggestions.suggestions),
      onMenuOpen: typeSuggestions.fetchSuggestions,
    },
    {
      label: "Risk Rating",
      activeCount: riskRatingFilters?.length || 0,
      menuOptions: quickMenuOptions(
        "riskRating",
        riskRatingSuggestions.suggestions,
      ),
      onMenuOpen: riskRatingSuggestions.fetchSuggestions,
    },
    ...(riskFactorSuggestions.suggestions.length
      ? [
          {
            label: "Risk Factor",
            activeCount: riskFactorFilters?.length || 0,
            menuOptions: quickMenuOptions(
              "riskFactor",
              riskFactorSuggestions.suggestions,
            ),
            onMenuOpen: riskFactorSuggestions.fetchSuggestions,
          },
        ]
      : []),
    {
      label: "Policy",
      activeCount: policyFilter ? 1 : 0,
      menuOptions: quickMenuOptions("policyMrn", policySuggestions.suggestions),
      onMenuOpen: policySuggestions.fetchSuggestions,
    },
    {
      label: "State",
      activeCount: stateFilter ? 1 : 0,
      menuOptions: quickMenuOptions("state", stateSuggestions.suggestions),
      onMenuOpen: stateSuggestions.fetchSuggestions,
    },
  ]);

  const activeFilterBar = renderActiveFilterBar(activeFilters, resetFilters);

  const activeFilterCount = activeFilters.length;

  const filterForm = renderFilterForm({
    filterBar,
    quickFilterBar,
    activeFilterBar,
  });

  const filterVariables: AssetAllFindingsFilterVariables = useMemo(() => {
    return {
      ...(typeFilters.length ? { types: typeFilters } : {}),
      ...(riskRatingFilters?.length ? { rating: riskRatingFilters } : {}),
      ...(riskFactorFilters?.length
        ? { riskFactors: { mrns: { or: riskFactorFilters } } }
        : {}),
      ...(policyFilter ? { policyMrn: policyFilter } : {}),
      ...(stateFilter ? { state: stateFilter } : {}),
      ...(searchFilters?.length ? { queryTerms: searchFilters } : {}),
    };
  }, [
    typeFilterParams,
    riskRatingFilterParams,
    riskFactorFilterParams,
    policyFilterParam,
    searchFilterParams,
  ]);

  return {
    filterVariables,
    filterForm,
    filterBar,
    quickFilterBar,
    activeFilterBar,
    activeFilterCount,
    resetFilters,
  };
}

function renderFilterBar(props: FilterBarProps) {
  return <FilterBar {...props} />;
}

function renderQuickFilterBar(quickFilters: QuickFilterButtonProps[]) {
  return (
    <QuickFilterBar>
      {quickFilters.map((quickFilterProps) => {
        const key = quickFilterProps.label;
        return <QuickFilterButton key={key} {...quickFilterProps} />;
      })}
    </QuickFilterBar>
  );
}

function renderActiveFilterBar(
  activeFilters: ActiveFilterButtonProps[],
  onResetClick: () => void,
) {
  const items = Object.entries(groupBy(activeFilters, "label")).flatMap(
    ([label, values], index) => {
      const buttons = values.map((props) => {
        const key = props.label + props.value;
        return <ActiveFilterButton key={key} {...props} />;
      });
      const separator = (
        <ChevronRightIcon
          key={label + "-separator"}
          sx={{ fontSize: "16px", color: "text.secondary" }}
        />
      );
      return [index === 0 ? [] : separator, ...buttons];
    },
  );
  return (
    items.length > 0 && (
      <ActiveFilterBar onResetClick={onResetClick}>{items}</ActiveFilterBar>
    )
  );
}

function renderFilterForm(props: FilterFormProps) {
  return <FilterForm {...props} />;
}

function useAllFindingsSuggestions(
  field: string,
  scopeMrn: string,
  baseQueryFilters: GetFindingsQueryVariables["filter"],
) {
  const typeSuggestions = useAllFindingsTypeSuggestions(
    scopeMrn,
    baseQueryFilters,
  );
  const riskRatingSuggestions = useAllFindingsRiskRatingSuggestions(
    scopeMrn,
    baseQueryFilters,
  );
  const riskFactorSuggestions = useAllFindingsRiskFactorSuggestions(
    scopeMrn,
    baseQueryFilters,
  );
  const policySuggestions = useAllFindingsPolicySuggestions(
    scopeMrn,
    baseQueryFilters,
  );
  const stateSuggestions = useAllFindingsStateSuggestions(
    scopeMrn,
    baseQueryFilters,
  );
  switch (field) {
    case "type":
      return typeSuggestions;
    case "riskRating":
      return riskRatingSuggestions;
    case "riskFactor":
      return riskFactorSuggestions;
    case "policyMrn":
      return policySuggestions;
    case "state":
      return stateSuggestions;
    default:
      return typeSuggestions;
  }
}

function useAllFindingsTypeSuggestions(
  resourceMrn: string,
  baseQueryFilters: GetFindingsQueryVariables["filter"],
) {
  const [getSuggestions, { data, previousData, loading, error }] =
    useGetFindingsFilterSuggestionsLazyQuery();

  const fetchSuggestions = (searchText?: string) => {
    return getSuggestions({
      variables: {
        filterType: FindingFilterType.Types,
        searchText,
        resourceMrn,
        filter: baseQueryFilters,
      },
    });
  };

  const param = "type";

  const suggestionsData =
    (data ?? previousData)?.findingsFilterSuggestions?.flatMap((suggestion) => {
      const value =
        suggestion.value.__typename === "FindingTypeValue"
          ? suggestion.value.findingTypeValue
          : "";

      if (!(ALLOWED_FINDING_TYPES as string[]).includes(value)) return [];

      return {
        param,
        field: `${param}.${value}`,
        value,
        label: formatTypeLabel(value),
        isMultiple: true,
      };
    }) || [];

  const loadingSuggestion = loadingMenuOption(param, `${param}.loading`);
  const emptySuggestion = emptyMenuOption(param, `${param}.empty`);

  const suggestions =
    suggestionsData.length > 0
      ? suggestionsData
      : loading
        ? [loadingSuggestion]
        : [emptySuggestion];

  return { suggestions, fetchSuggestions, loading, error };
}

function useAllFindingsRiskRatingSuggestions(
  resourceMrn: string,
  baseQueryFilters: GetFindingsQueryVariables["filter"],
) {
  const [getSuggestions, { data, previousData, loading, error }] =
    useGetFindingsFilterSuggestionsLazyQuery();

  const fetchSuggestions = (searchText?: string) => {
    return getSuggestions({
      variables: {
        filterType: FindingFilterType.Rating,
        searchText,
        resourceMrn,
        filter: baseQueryFilters,
      },
    });
  };

  const param = "riskRating";

  const suggestionsData =
    (data ?? previousData)?.findingsFilterSuggestions?.flatMap(
      (suggestion, index) => {
        const value =
          suggestion.value.__typename === "ScoreRatingValue"
            ? suggestion.value.ratingValue
            : "";
        return {
          param,
          field: `${param}.${index}`,
          value: value,
          label: formatRiskRatingLabel(suggestion.label),
          isMultiple: true,
        };
      },
    ) || [];

  const loadingSuggestion = loadingMenuOption(param, `${param}.loading`);
  const emptySuggestion = emptyMenuOption(param, `${param}.empty`);

  const suggestions =
    suggestionsData.length > 0
      ? suggestionsData
      : loading
        ? [loadingSuggestion]
        : [emptySuggestion];

  return {
    suggestions,
    fetchSuggestions,
    loading,
    error,
  };
}

function useAllFindingsRiskFactorSuggestions(
  resourceMrn: string,
  baseQueryFilters: GetFindingsQueryVariables["filter"],
) {
  const [getSuggestions, { data, previousData, loading, error }] =
    useGetFindingsFilterSuggestionsLazyQuery();

  const fetchSuggestions = (searchText?: string) => {
    return getSuggestions({
      variables: {
        filterType: FindingFilterType.RiskFactors,
        searchText,
        resourceMrn,
        filter: baseQueryFilters,
      },
    });
  };

  const param = "riskFactorMrn";

  const suggestionsData =
    (data ?? previousData)?.findingsFilterSuggestions?.flatMap(
      (suggestion, index) => {
        const value =
          suggestion.value.__typename === "StringValue"
            ? suggestion.value.stringValue
            : "";
        return {
          param,
          field: `${param}.${index}`,
          value: value,
          label: suggestion.label,
          isMultiple: true,
        };
      },
    ) || [];

  const loadingSuggestion = loadingMenuOption(param, `${param}.loading`);
  const emptySuggestion = emptyMenuOption(param, `${param}.empty`);

  const suggestions =
    suggestionsData.length > 0
      ? suggestionsData
      : loading
        ? [loadingSuggestion]
        : [emptySuggestion];

  return {
    suggestions,
    fetchSuggestions,
    loading,
    error,
  };
}

function useAllFindingsPolicySuggestions(
  resourceMrn: string,
  baseQueryFilters: GetFindingsQueryVariables["filter"],
) {
  const [getSuggestions, { data, previousData, loading, error }] =
    useGetFindingsFilterSuggestionsLazyQuery();

  const fetchSuggestions = (searchText?: string) => {
    return getSuggestions({
      variables: {
        filterType: FindingFilterType.PolicyMrn,
        searchText,
        resourceMrn,
        filter: baseQueryFilters,
      },
    });
  };

  const param = "policyMrn";

  const suggestionsData =
    (data ?? previousData)?.findingsFilterSuggestions?.flatMap(
      (suggestion, index) => {
        const value =
          suggestion.value.__typename === "StringValue"
            ? suggestion.value.stringValue
            : "";
        return {
          param,
          field: `${param}.${index}`,
          value: value,
          label: suggestion.label,
        };
      },
    ) || [];

  const loadingSuggestion = loadingMenuOption(param, `${param}.loading`);
  const emptySuggestion = emptyMenuOption(param, `${param}.empty`);

  const suggestions =
    suggestionsData.length > 0
      ? suggestionsData
      : loading
        ? [loadingSuggestion]
        : [emptySuggestion];

  return {
    suggestions,
    fetchSuggestions,
    loading,
    error,
  };
}

function useAllFindingsStateSuggestions(
  resourceMrn: string,
  baseQueryFilters: GetFindingsQueryVariables["filter"],
) {
  const [getSuggestions, { data, previousData, loading, error }] =
    useGetFindingsFilterSuggestionsLazyQuery();

  const fetchSuggestions = (searchText?: string) => {
    return getSuggestions({
      variables: {
        filterType: FindingFilterType.State,
        searchText,
        resourceMrn,
        filter: baseQueryFilters,
      },
    });
  };

  const param = "state";

  const suggestionsData =
    (data ?? previousData)?.findingsFilterSuggestions?.flatMap(
      (suggestion, index) => {
        const value =
          suggestion.value.__typename === "ScoreStateFilterValue"
            ? suggestion.value.scoreStateValue
            : "";
        return {
          param,
          field: `${param}.${index}`,
          value: value,
          label: formatStateLabel(suggestion.label),
        };
      },
    ) || [];

  const loadingSuggestion = loadingMenuOption(param, `${param}.loading`);
  const emptySuggestion = emptyMenuOption(param, `${param}.empty`);

  const suggestions =
    suggestionsData.length > 0
      ? suggestionsData
      : loading
        ? [loadingSuggestion]
        : [emptySuggestion];

  return {
    suggestions,
    fetchSuggestions,
    loading,
    error,
  };
}

function isScoreRating(value: any): value is ScoreRating {
  return Object.values(ScoreRating).includes(value);
}

function formatRiskRatingLabel(label: string) {
  return upperFirst(label.toLocaleLowerCase());
}

function formatRiskFactorLabel(label: string) {
  return label.split("/").pop() || label;
}

function formatPolicyLabel(label: string) {
  return label.split("/").pop() || label;
}

function formatStateLabel(label: string) {
  return (
    label
      .toLowerCase()
      .split("_")
      .map((s) => upperFirst(s))
      .join(" ") || label
  );
}

function formatTypeLabel(type: string) {
  switch (type) {
    case FindingType.Check:
      return "Check";
    case FindingType.Cve:
      return "Vulnerability";
    case FindingType.Advisory:
      return "Advisory";
    case FindingType.Informational:
      return "Informational";
    case FindingType.Malware:
      return "Malware";
    case FindingType.Security:
      return "Security";
    case FindingType.Threat:
      return "Threat";
    default:
      return type;
  }
}

function isScoreStateFilter(value: any): value is ScoreStateFilter {
  return Object.values(ScoreStateFilter).includes(value);
}
